import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  applyClientSideTableState,
  DataTable,
  useTableState,
  type ColumnDef,
} from '@/components/data-table'
import { useHaloUsers } from '@/features/halocrm-user/hooks'
import { useVoivodeships } from '@/features/voivodeship/hooks'
import { useCustomers } from '../hooks'
import type { Customer } from '../types'
import { customerDisplayName } from '../types'
import { CustomerTypeBadge } from './CustomerTypeBadge'
import { CustomerStatusBadge } from './CustomerStatusBadge'

const TABLE_ID = 'customers'
// Trzymamy 200 dopóki backend nie przyjmie wyższego limitu (ListCustomersUseCase.MAX_LIMIT).
// Po restarcie backendu z bumped MAX_LIMIT można podnieść do 5000.
const FETCH_LIMIT = 200

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  } catch {
    return iso
  }
}

function customerIdentifier(c: Customer): string {
  if (c.type === 'COMPANY') return c.nip ?? '-'
  return c.pesel ?? '-'
}

function buildColumns(
  assigneeNames: Map<number, string>,
  voivodeshipLabels: Map<string, string>,
): ColumnDef<Customer>[] {
  return [
    {
      key: 'code',
      header: 'Kod',
      sortable: true,
      filter: { kind: 'text', placeholder: 'Filtruj kod...' },
      accessor: (c) => c.code ?? `C-${c.id}`,
      className: 'font-mono text-xs whitespace-nowrap',
      alwaysVisible: true,
    },
    {
      key: 'displayName',
      header: 'Imię / Nazwa',
      sortable: true,
      filter: { kind: 'text', placeholder: 'Filtruj nazwę...' },
      accessor: (c) => <span className="font-medium">{customerDisplayName(c)}</span>,
      alwaysVisible: true,
    },
    {
      key: 'type',
      header: 'Typ',
      sortable: true,
      filter: {
        kind: 'select',
        options: [
          { value: 'PERSON', label: 'Osoba' },
          { value: 'COMPANY', label: 'Firma' },
        ],
      },
      accessor: (c) => <CustomerTypeBadge type={c.type} />,
      className: 'w-24',
    },
    {
      key: 'identifier',
      header: 'PESEL / NIP',
      filter: { kind: 'text', placeholder: 'Filtruj PESEL/NIP...' },
      accessor: (c) => <span className="font-mono text-xs">{customerIdentifier(c)}</span>,
      className: 'whitespace-nowrap',
    },
    {
      key: 'voivodeshipCode',
      header: 'Województwo',
      sortable: true,
      filter: { kind: 'text' },
      accessor: (c) =>
        c.voivodeshipCode ? voivodeshipLabels.get(c.voivodeshipCode) ?? c.voivodeshipCode : '-',
    },
    {
      key: 'assigneeUserId',
      header: 'Opiekun',
      sortable: true,
      accessor: (c) => {
        if (c.assigneeUserId == null) return '-'
        const name = assigneeNames.get(c.assigneeUserId)
        return name ?? `#${c.assigneeUserId}`
      },
    },
    {
      key: 'updatedAt',
      header: 'Aktualizacja',
      sortable: true,
      filter: { kind: 'dateRange' },
      accessor: (c) => <span className="text-muted-foreground">{formatDate(c.updatedAt)}</span>,
      className: 'whitespace-nowrap',
    },
    {
      key: 'isActive',
      header: 'Status',
      sortable: true,
      filter: { kind: 'bool', trueLabel: 'Aktywni', falseLabel: 'Nieaktywni' },
      accessor: (c) => <CustomerStatusBadge active={c.isActive} />,
      className: 'w-28',
    },
  ]
}

export function CustomerTable() {
  const navigate = useNavigate()
  const { state, setSort, setSearch, setFilter, removeFilter, clearFilters, setPage } =
    useTableState(TABLE_ID)
  const { data, isLoading, isError } = useCustomers(0, FETCH_LIMIT)
  const haloUsers = useHaloUsers({ active: false, limit: 200 })
  const { data: voivodeships } = useVoivodeships()

  const assigneeNames = useMemo(() => {
    const map = new Map<number, string>()
    haloUsers.data?.items.forEach((u) => map.set(u.id, u.fullName))
    return map
  }, [haloUsers.data])

  const voivodeshipLabels = useMemo(() => {
    const map = new Map<string, string>()
    voivodeships?.forEach((v) => map.set(v.code, v.labelPl))
    return map
  }, [voivodeships])

  const columns = useMemo(
    () => buildColumns(assigneeNames, voivodeshipLabels),
    [assigneeNames, voivodeshipLabels],
  )

  const filteredRows = useMemo(
    () =>
      applyClientSideTableState(data?.items ?? [], columns, state, {
        valueGetters: {
          code: (c) => c.code ?? `C-${c.id}`,
          displayName: customerDisplayName,
          type: (c) => c.type,
          identifier: customerIdentifier,
          voivodeshipCode: (c) =>
            c.voivodeshipCode ? voivodeshipLabels.get(c.voivodeshipCode) ?? c.voivodeshipCode : '',
          assigneeUserId: (c) =>
            c.assigneeUserId != null ? assigneeNames.get(c.assigneeUserId) ?? `#${c.assigneeUserId}` : '',
          updatedAt: (c) => c.updatedAt.slice(0, 10),
          isActive: (c) => c.isActive,
        },
        searchString: (c) => {
          const ident = customerIdentifier(c)
          const voiv = c.voivodeshipCode ? voivodeshipLabels.get(c.voivodeshipCode) ?? c.voivodeshipCode : ''
          return `${c.code ?? ''} ${customerDisplayName(c)} ${ident} ${voiv}`.toLowerCase()
        },
      }),
    [data, columns, state, assigneeNames, voivodeshipLabels],
  )

  const pageRows = useMemo(
    () => filteredRows.slice(state.pageOffset, state.pageOffset + state.pageLimit),
    [filteredRows, state.pageOffset, state.pageLimit],
  )

  const pagination = useMemo(
    () => ({ offset: state.pageOffset, limit: state.pageLimit, total: filteredRows.length }),
    [state.pageOffset, state.pageLimit, filteredRows.length],
  )

  return (
    <DataTable<Customer>
      tableId={TABLE_ID}
      data={pageRows}
      columns={columns}
      rowKey={(c) => c.id}
      isLoading={isLoading}
      isError={isError}
      sort={state.sort}
      onSortChange={setSort}
      state={state}
      searchable
      searchPlaceholder="Szukaj kodu / nazwy / PESEL / NIP / regionu..."
      onSearchChange={setSearch}
      onFilterChange={setFilter}
      onFilterRemove={removeFilter}
      onClearAll={clearFilters}
      pagination={pagination}
      onPaginationChange={setPage}
      onRowClick={(c) => navigate(`/clients/${c.id}`)}
    />
  )
}
